import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, CheckCircle2, Download, Sparkles } from "lucide-react";
import ebooksImage from "@/assets/ebooks.jpg";

const ebooks = [
  {
    title: "Guía de composición fotográfica",
    description:
      "Aprende a ordenar el encuadre, dirigir la mirada y crear fotografías que cuenten una historia con intención.",
    href: "https://drive.google.com/uc?export=download&id=18XBNwfh1VMFT8rSaZ0E3aRJmv7k6wv57",
  },
  {
    title: "Los secretos de la nitidez en fotografía",
    description:
      "Descubre cómo mejorar el enfoque, la estabilidad y la técnica para conseguir imágenes más limpias y definidas.",
    href: "https://drive.google.com/uc?export=download&id=1axY3aCNELdkEtaNp9_KIhmyKOaCaaSD3",
  },
];

export const Route = createFileRoute("/descarga-ebooks")({
  head: () => ({
    meta: [
      { title: "Tus ebooks de fotografía · FotoPasión" },
      {
        name: "description",
        content:
          "Descarga la guía de composición fotográfica y Los secretos de la nitidez en fotografía de FotoPasión.",
      },
      { name: "robots", content: "noindex, nofollow, noarchive, nosnippet" },
      { name: "googlebot", content: "noindex, nofollow, noarchive, nosnippet" },
    ],
  }),
  component: DescargaEbooks,
});

function DescargaEbooks() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground">
      <header className="border-b border-border/60 bg-background/90">
        <div className="mx-auto flex max-w-7xl items-center justify-center px-6 py-4 md:px-8">
          <div className="flex items-center gap-3" aria-label="FotoPasión">
            <img src="/logo.png" alt="FotoPasión" width={96} height={96} className="h-12 w-auto" />
            <span className="font-display text-xl font-bold">FotoPasión</span>
          </div>
        </div>
      </header>

      <main>
        <section
          className="relative overflow-hidden"
          style={{ background: "var(--gradient-hero)" }}
        >
          <div className="absolute inset-0" style={{ background: "var(--gradient-hero-glow)" }} />
          <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-6 py-14 md:px-8 md:py-20 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/75 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary backdrop-blur">
                <Sparkles className="h-4 w-4" /> Tus regalos están listos
              </span>
              <h1 className="mt-5 text-balance font-display text-4xl font-bold leading-[1.08] tracking-tight md:text-5xl lg:text-6xl">
                Gracias por visitar <span className="italic text-primary">FotoPasión</span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                Esperamos que estos ebooks te ayuden a mirar con más intención, dominar mejor tu
                cámara y disfrutar todavía más de la fotografía.
              </p>
              <div className="mt-7 flex items-start gap-3 rounded-2xl border border-primary/15 bg-white/70 p-4 text-sm leading-6 text-muted-foreground backdrop-blur">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <p>
                  Puedes descargar los dos recursos a continuación y guardarlos para consultarlos
                  siempre que los necesites.
                </p>
              </div>
            </div>

            <div className="relative">
              <div
                className="absolute -inset-5 rounded-[2.5rem] opacity-40 blur-3xl"
                style={{ background: "var(--gradient-primary)" }}
              />
              <img
                src={ebooksImage}
                alt="Guía de composición fotográfica y Los secretos de la nitidez"
                width={1536}
                height={1024}
                fetchPriority="high"
                className="relative aspect-[3/2] w-full rounded-[2rem] border-8 border-white/70 object-cover shadow-[var(--shadow-soft)]"
              />
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-6 py-16 md:px-8 md:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              Biblioteca gratuita
            </span>
            <h2 className="mt-2 text-balance font-display text-3xl font-bold md:text-5xl">
              Descarga tus ebooks
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Empieza por el tema que más te interese o descarga ambos para avanzar a tu ritmo.
            </p>
          </div>

          <div className="mt-10 grid gap-6 md:mt-14 md:grid-cols-2">
            {ebooks.map((ebook) => (
              <article
                key={ebook.title}
                className="flex flex-col rounded-[2rem] border border-border bg-card p-7 shadow-sm transition hover:-translate-y-1 hover:border-primary/35 hover:shadow-[var(--shadow-soft)] md:p-9"
              >
                <span className="grid h-14 w-14 place-items-center rounded-2xl bg-secondary text-cta">
                  <BookOpen className="h-7 w-7" />
                </span>
                <p className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                  Ebook gratuito
                </p>
                <h3 className="mt-2 text-balance font-display text-2xl font-bold leading-tight md:text-3xl">
                  {ebook.title}
                </h3>
                <p className="mt-4 flex-1 text-base leading-7 text-muted-foreground">
                  {ebook.description}
                </p>
                <a
                  href={ebook.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-cta px-6 py-3.5 font-semibold text-cta-foreground shadow-sm transition hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  Descargar ebook <Download className="h-4 w-4" />
                </a>
              </article>
            ))}
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Las descargas se sirven de forma segura desde Google Drive.
          </p>
        </section>

        <section className="px-6 pb-16 md:px-8 md:pb-24">
          <div className="mx-auto max-w-5xl overflow-hidden rounded-[2rem] bg-secondary px-7 py-10 text-center text-secondary-foreground shadow-[var(--shadow-soft)] md:px-12 md:py-14">
            <span className="text-sm font-semibold uppercase tracking-wider text-cta">
              Sigue aprendiendo
            </span>
            <h2 className="mx-auto mt-2 max-w-3xl text-balance font-display text-3xl font-bold md:text-4xl">
              Lleva lo aprendido a la práctica con una formación ordenada
            </h2>
            <p className="mx-auto mt-4 max-w-2xl leading-7 text-secondary-foreground/75">
              Cuando quieras profundizar, en nuestros cursos encontrarás explicaciones claras,
              ejercicios prácticos y una ruta pensada para avanzar paso a paso.
            </p>
            <Link
              to="/"
              hash="cursos"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-cta px-7 py-3.5 font-semibold text-cta-foreground transition hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cta"
            >
              Ver los cursos de fotografía <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>

      <footer className="bg-foreground text-background">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 px-6 py-9 text-center md:px-8">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="" width={96} height={96} className="h-9 w-auto" />
            <span className="font-display text-lg font-bold">FotoPasión</span>
          </div>
          <p className="text-sm text-background/65">
            Esperamos que aprendas mucho y disfrutes del camino.
          </p>
        </div>
      </footer>
    </div>
  );
}
